/* ==========================================================================
   PhotoSwipe (v4) Lightbox with Thumbnails — Behavior
   Pairs with: snippets/product-pwsp-lightbox.liquid
               assets/photoswipe-lightbox.css

   Usage (auto-init, see bottom of file):
     Any element matching `.pswp` on the page will be turned into a
     ProductPhotoswipeLightbox instance automatically once the DOM is ready.
     Trigger elements need class="prlightbox" and a child <img> carrying:
       data-media-type   (unused placeholder for future item types)
       data-slide-index  index to open the lightbox at
       data-size          "WIDTHxHEIGHT"
       data-full-image    full-size image URL
       data-video-html    (optional) raw video/iframe embed HTML

   Notes:
     - No Liquid/template logic lives in this file on purpose, so it can be
       served as a normal cached theme asset. Whether the lightbox is enabled
       at all is decided in the Liquid snippet (it simply omits the
       <script src="..."> tag when disabled).
     - Requires jQuery and the global `PhotoSwipe` / `PhotoSwipeUI_Default`
       constructors (loaded via photoswipe.min.js).
   ========================================================================== */

(function ($, window, document) {
    'use strict';

    var SELECTORS = {
        root: '.pswp',
        trigger: '.prlightbox',
        thumbnailItem: '.thumbnail-item',
        thumbnailsList: '.pswp__thumbnails-list',
        thumbnailsWrapper: '.pswp__thumbnails-wrapper',
        thumbnail: '.pswp__thumbnail',
        thumbnailButton: '.pswp__thumbnail button',
        thumbnailsPanel: '.pswp__thumbnails',
        activeIndicator: '.thumbnail-active-indicator',
        thumbnailsToggleButton: '.pswp__button--thumbnails',
        // Rendered by PhotoSwipe itself from the shareButtons option below
        // (id: 'copy-link' -> class "pswp__share--copy-link"), alongside
        // the Facebook/Twitter/Pinterest/download links in the same panel.
        copyLinkShareOption: '.pswp__share--copy-link'
    };

    var CLASSES = {
        bodyOpen: 'pswp-open',
        thumbnailsHiddenPanel: 'pswp__thumbnails--hidden',
        thumbnailsHiddenRoot: 'pswp--thumbnails-hidden',
        thumbnailActive: 'pswp__thumbnail--active',
        thumbnailVideo: 'pswp__thumbnail--video',
        fadeIn: 'pswp__item--fade-in',
        fadeInFast: 'pswp__item--fade-in-fast'
    };

    /**
    * Copies text to the clipboard, falling back to the legacy
    * execCommand approach when the modern Clipboard API isn't available -
    * notably, navigator.clipboard only exists in secure contexts (https
    * or localhost), and this control is also served over plain http on
    * the internal JungleGears site.
    * @param {string} text
    * @returns {Promise<void>}
    */
    function copyTextToClipboard(text) {
        if (navigator.clipboard && window.isSecureContext) {
            return navigator.clipboard.writeText(text);
        }

        return new Promise(function (resolve, reject) {
            var textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.top = '-1000px';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();

            try {
                var succeeded = document.execCommand('copy');
                document.body.removeChild(textarea);
                if (succeeded) {
                    resolve();
                } else {
                    reject(new Error('document.execCommand("copy") returned false'));
                }
            } catch (err) {
                document.body.removeChild(textarea);
                reject(err);
            }
        });
    }

    // Query param used both to build the lightbox's Share-button link (see
    // _buildShareUrlForCurrentSlide) and to read it back on page load (see
    // _openFromShareLinkIfPresent), so a shared link reopens on the same
    // slide it was shared from. Server-side Open Graph tags for the share
    // preview card should key off this same param name.
    var SHARE_SLIDE_PARAM = 'slide';

    /**
     * One instance manages a single `.pswp` root element: building the slide
     * list, rendering/syncing the thumbnail strip, and handling video playback.
     *
     * @param {string|Element|jQuery} rootSelector
     */
    function ProductPhotoswipeLightbox(rootSelector) {
        this.$pswp = $(rootSelector).first();
        this.pswpEl = this.$pswp.get(0);

        this.lightBox = null;
        this.items = [];
        this.thumbnailsData = [];
        this.videoPlaybackStates = {};
        this.thumbnailsVisible = true;
        this.lastIndex = 0;
    }

    /* ------------------------------------------------------------------------
       Public API
       ------------------------------------------------------------------------ */

    /** Bind trigger clicks. Call once per page load. */
    ProductPhotoswipeLightbox.prototype.init = function () {
        if (!this.pswpEl) {
            return this;
        }

        var self = this;

        $(document).on('click', SELECTORS.trigger, function (event) {
            event.preventDefault();
            self.open(event.currentTarget);
        });

        // Allows other scripts to re-bind triggers after AJAX content swaps
        // (e.g. variant image swaps that regenerate .prlightbox markup).
        $(document).on('rebind-photoswipe', function () {
            $(SELECTORS.trigger)
                .off('click.pswp')
                .on('click.pswp', function (event) {
                    event.preventDefault();
                    self.open(event.currentTarget);
                });
        });

        $(window).on('resize', function () {
            self._onViewportChange();
        });

        $(window).on('orientationchange', function () {
            setTimeout(function () {
                self._onViewportChange();
            }, 300); // orientation change needs a longer settle delay
        });

        // Native addEventListener with capture:true, not jQuery's (bubble-
        // phase) .on() - and bound once here, not per-open. PhotoSwipe's own
        // share-panel click handling is a raw `.onclick =` assignment on
        // .pswp__share-tooltip (see _openWindowPopup in
        // photoswipe-ui-default.js), reassigned every time that panel's
        // content is rebuilt (on every slide change, to keep share URLs
        // current) - so a listener bound once per-open to a specific <a>
        // element won't survive that, and a bubble-phase listener anywhere
        // else can't reliably run before an ancestor's .onclick regardless
        // of registration order. A capture-phase listener on document is
        // guaranteed to run first, and delegation means it keeps working
        // no matter how many times the share panel's markup is rebuilt.
        document.addEventListener('click', function (event) {
            var copyLinkEl = event.target.closest && event.target.closest(SELECTORS.copyLinkShareOption);
            if (copyLinkEl) {
                event.preventDefault();
                event.stopPropagation();
                self._copyShareLink(copyLinkEl);
            }
        }, true);

        return this;
    };

    /**
     * Open the lightbox, starting at the slide referenced by the clicked
     * element's `data-slide-index`.
     * @param {Element} [clickedElement]
     */
    ProductPhotoswipeLightbox.prototype.open = function (clickedElement) {
        var startIndex = this._resolveStartIndex(clickedElement);
        this.openAtIndex(startIndex);
    };

    /**
     * Opens the lightbox at a specific slide index directly, with no
     * clicked trigger element involved. Used by open() (click path) above,
     * and by _openFromShareLinkIfPresent() below for the ?slide= deep link.
     * @param {number} startIndex
     */
    ProductPhotoswipeLightbox.prototype.openAtIndex = function (startIndex) {
        var self = this;

        this.items = this._buildItems();

        var options = {
            index: startIndex,
            bgOpacity: 1,
            showHideOpacity: true,
            closeOnScroll: false,
            closeOnVerticalDrag: false,
            history: false,
            focus: false,
            getPageURLForShare: function () {
                return self._buildShareUrlForCurrentSlide();
            },
            // PhotoSwipe v4's own defaults, plus a "Copy Link" entry at the
            // end. Its url is still a real link to this same share URL (via
            // {{url}}) as a no-JS fallback; the click handler bound in
            // _bindCopyLinkButton() intercepts it to copy instead of
            // navigating.
            shareButtons: [
                { id: 'facebook', label: 'Share on Facebook', url: 'https://www.facebook.com/sharer/sharer.php?u={{url}}' },
                { id: 'twitter', label: 'Tweet', url: 'https://twitter.com/intent/tweet?text={{text}}&url={{url}}' },
                { id: 'pinterest', label: 'Pin it', url: 'http://www.pinterest.com/pin/create/button/?url={{url}}&media={{image_url}}&description={{text}}' },
                { id: 'download', label: 'Download image', url: '{{raw_image_url}}', download: true },
                { id: 'copy-link', label: 'Copy Link', url: '#' }
            ]
        };

        this.lightBox = new PhotoSwipe(this.pswpEl, PhotoSwipeUI_Default, this.items, options);

        this._renderThumbnails();
        this._bindLightboxEvents();
        this.lightBox.init();
        this._bindThumbnailsToggleButton();
    };

    /**
     * Builds the URL the Share button's Facebook/Twitter/etc. links point
     * to, encoding the currently-open slide as ?slide=N so a recipient
     * lands on this exact image/video rather than the page's default. Pairs
     * with _openFromShareLinkIfPresent() and with server-side Open Graph
     * tags keyed off SHARE_SLIDE_PARAM.
     * @returns {string}
     */
    ProductPhotoswipeLightbox.prototype._buildShareUrlForCurrentSlide = function () {
        var index = this.lightBox ? this.lightBox.getCurrentIndex() : 0;
        var url = new URL(window.location.href);
        url.searchParams.set(SHARE_SLIDE_PARAM, index);
        return url.toString();
    };

    /**
     * If the page loaded with ?slide=N (e.g. a link from the Share button),
     * opens the lightbox at that slide automatically. Does nothing if the
     * param is absent, not a number, or out of range for the current media
     * (e.g. a stale link shared before the product's photos changed).
     */
    ProductPhotoswipeLightbox.prototype._openFromShareLinkIfPresent = function () {
        var params = new URLSearchParams(window.location.search);
        if (!params.has(SHARE_SLIDE_PARAM)) {
            return;
        }

        var index = parseInt(params.get(SHARE_SLIDE_PARAM), 10);
        var itemCount = this._buildItems().length;

        if (isNaN(index) || index < 0 || index >= itemCount) {
            return;
        }

        this.openAtIndex(index);
    };

    /* ------------------------------------------------------------------------
       Slide / thumbnail data
       ------------------------------------------------------------------------ */

    /** @returns {Array<Object>} PhotoSwipe-compatible item list built from the DOM */
    ProductPhotoswipeLightbox.prototype._buildItems = function () {
        var items = [];
        var thumbnailsData = [];

        // Exclude slick-cloned or swiper-cloned items if applicable
        $(SELECTORS.thumbnailItem).not('.slick-cloned, .swiper-slide-duplicate').each(function () {
            var $img = $(this).find('img');
            if ($img.length === 0) {
                return;
            }

            var videoHtml = $img.data('video-html');
            var fullImage = $img.data('full-image');

            // Skip incomplete/placeholder markup (e.g. carousel loop-clones that
            // duplicate .thumbnail-item without the real slide data attributes).
            // Pushing a blank entry here would desync this array's indexes from
            // the data-slide-index values baked into the trigger markup.
            if (!videoHtml && !fullImage) {
                return;
            }

            var size = ($img.data('size') || '').split('x');
            var width = parseInt(size[0], 10) || 0;
            var height = parseInt(size[1], 10) || 0;
            var thumbnailSrc = $img.attr('src');
            var isVideo = Boolean(videoHtml);

            if (isVideo) {
                items.push({
                    html: videoHtml,
                    w: width || 1920,
                    h: height || 1080,
                    isVideo: true
                });
            } else {
                items.push({
                    src: fullImage,
                    w: width || 1600,
                    h: height || 1600,
                    isVideo: false
                });
            }

            thumbnailsData.push({ src: thumbnailSrc, isVideo: isVideo });
        });

        this.thumbnailsData = thumbnailsData;
        return items;
    };

    ProductPhotoswipeLightbox.prototype._resolveStartIndex = function (clickedElement) {
        if (!clickedElement || !$(clickedElement).hasClass('prlightbox')) {
            return 0;
        }
        var slideIndex = parseInt($(clickedElement).find('img').attr('data-slide-index'), 10);
        return isNaN(slideIndex) ? 0 : slideIndex;
    };

    /* ------------------------------------------------------------------------
       Thumbnail strip rendering + interaction
       ------------------------------------------------------------------------ */

    ProductPhotoswipeLightbox.prototype._renderThumbnails = function () {
        var $list = $(SELECTORS.thumbnailsList);
        if ($list.length === 0) {
            return;
        }

        var html = this.thumbnailsData.map(function (thumb, index) {
            var videoClass = thumb.isVideo ? ' ' + CLASSES.thumbnailVideo : '';
            var playIcon = thumb.isVideo ? '<i class="ad ad-play" aria-hidden="true"></i>' : '';
            var label = thumb.isVideo ? 'Play video ' + (index + 1) : 'View image ' + (index + 1);
            return (
                '<li class="pswp__thumbnail' + videoClass + '" data-index="' + index + '">' +
                '<button type="button" aria-label="' + label + '">' +
                '<img src="' + thumb.src + '" alt="" aria-hidden="true">' +
                playIcon +
                '</button>' +
                '</li>'
            );
        }).join('');

        $list.html(html);
        this._bindThumbnailEvents();
    };

    ProductPhotoswipeLightbox.prototype._bindThumbnailEvents = function () {
        var self = this;

        $(SELECTORS.thumbnail).each(function () {
            var $thumb = $(this);
            var $button = $thumb.find('button');
            var index = parseInt($thumb.attr('data-index'), 10);

            // A real <button> already gets click via mouse, keyboard (Enter/Space),
            // and tap for free — no separate touchstart/touchend hack needed.
            $button.on('click', function (event) {
                event.preventDefault();
                self._goToSlide(index);
            });
        });
    };

    ProductPhotoswipeLightbox.prototype._goToSlide = function (index) {
        if (this.lightBox && !isNaN(index)) {
            this.lightBox.goTo(index);
            var self = this;
            setTimeout(function () {
                self._moveIndicator(index);
            }, 100);
        }
    };

    ProductPhotoswipeLightbox.prototype._setActiveThumbnail = function (index) {
        const self = this;	// save instance reference

        $(SELECTORS.thumbnail).each(function (i) {
            var $thumb = $(this);
            var isActive = i === index;

            $thumb.toggleClass(CLASSES.thumbnailActive, isActive);
            $thumb.find('button').attr('aria-current', isActive ? 'true' : 'false');

            if (isActive) {
                self._scrollThumbnailIntoView($thumb.get(0));
            }
        }.bind(this));
    };

    ProductPhotoswipeLightbox.prototype._scrollThumbnailIntoView = function (thumbnailEl) {
        var $list = $(SELECTORS.thumbnailsList);

        // Ensure we have a raw DOM element (unwrap jQuery object if passed)
        var el = thumbnailEl instanceof $ ? thumbnailEl.get(0) : thumbnailEl;

        if ($list.length === 0 || !el || typeof el.getBoundingClientRect !== 'function') {
            return;
        }

        var containerRect = $list.get(0).getBoundingClientRect();
        var thumbRect = el.getBoundingClientRect();
        var scrollLeft = $list.scrollLeft();

        if (thumbRect.left < containerRect.left) {
            $list.scrollLeft(scrollLeft - (containerRect.left - thumbRect.left) - 20);
        } else if (thumbRect.right > containerRect.right) {
            $list.scrollLeft(scrollLeft + (thumbRect.right - containerRect.right) + 20);
        }
    };

    /** Slides the active-indicator box under the given thumbnail index. */
    ProductPhotoswipeLightbox.prototype._moveIndicator = function (index) {
        var $thumbnails = $(SELECTORS.thumbnail);
        var $indicator = $(SELECTORS.activeIndicator);
        var $container = $(SELECTORS.thumbnailsWrapper);
        var $list = $(SELECTORS.thumbnailsList);

        var thumbnailEl = $thumbnails.get(index);
        if (!thumbnailEl || $indicator.length === 0 || $container.length === 0) {
            return;
        }

        this._scrollThumbnailIntoView(thumbnailEl);

        var positionIndicator = function () {
            var containerRect = $container.get(0).getBoundingClientRect();
            var thumbnailRect = thumbnailEl.getBoundingClientRect();
            var relativeLeft = thumbnailRect.left - containerRect.left;
            $indicator.css('transform', 'translateX(' + relativeLeft + 'px)');
        };

        // Wait for the scroll (if any) to settle before measuring position.
        var scrollTimer;
        var onScroll = function () {
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(function () {
                positionIndicator();
                $list.off('scroll', onScroll);
            }, 100);
        };

        $list.on('scroll', onScroll);
        setTimeout(positionIndicator, 200); // fallback if no scroll event fires
    };

    ProductPhotoswipeLightbox.prototype._toggleThumbnailsPanel = function () {
        var $panel = $(SELECTORS.thumbnailsPanel);

        this.thumbnailsVisible = !this.thumbnailsVisible;
        $panel.toggleClass(CLASSES.thumbnailsHiddenPanel, !this.thumbnailsVisible);
        this.$pswp.toggleClass(CLASSES.thumbnailsHiddenRoot, !this.thumbnailsVisible);

        // Let the CSS changes apply before PhotoSwipe recalculates its layout.
        var self = this;
        setTimeout(function () {
            if (self.lightBox) {
                self.lightBox.updateSize();
            }
        }, 100);
    };

    ProductPhotoswipeLightbox.prototype._bindThumbnailsToggleButton = function () {
        var self = this;
        setTimeout(function () {
            $(SELECTORS.thumbnailsToggleButton)
                .off('click.pswpToggle touchend.pswpToggle')
                .on('click.pswpToggle touchend.pswpToggle', function (event) {
                    event.preventDefault();
                    event.stopPropagation();
                    self._toggleThumbnailsPanel();
                });
        }, 100);
    };


    /**
     * Copies the current slide's share URL (see _buildShareUrlForCurrentSlide)
     * to the clipboard and briefly swaps the "Copy Link" label to "Copied!"
     * as confirmation, matching how the other share options in this same
     * panel are plain text links. Falls back to a native prompt if the
     * clipboard write itself fails (e.g. the user denied clipboard
     * permission), so the link is still available to copy manually rather
     * than silently doing nothing.
     * @param {Element} linkEl
     */
    ProductPhotoswipeLightbox.prototype._copyShareLink = function (linkEl) {
        let $link = $(linkEl);
        const originalLabel = $link.data('copyLinkOriginalLabel') || $link.text();
        const url = this._buildShareUrlForCurrentSlide();

        copyTextToClipboard(url).then(function () {
            clearTimeout($link.data('copyLinkTimeoutId'));
            $link.data('copyLinkOriginalLabel', originalLabel);
            $link.text('Copied!');
            const timeoutId = setTimeout(function () {
                $link.text(originalLabel);
            }, 1600);
            $link.data('copyLinkTimeoutId', timeoutId);
        }).catch(function () {
            window.prompt('Copy this link:', url);
        });
    };

    ProductPhotoswipeLightbox.prototype._onViewportChange = function () {
        if (this.lightBox && this.lightBox.getCurrentIndex) {
            var self = this;
            setTimeout(function () {
                self._moveIndicator(self.lightBox.getCurrentIndex());
            }, 100);
        }
    };

    /* ------------------------------------------------------------------------
       PhotoSwipe lifecycle event wiring
       ------------------------------------------------------------------------ */

    ProductPhotoswipeLightbox.prototype._bindLightboxEvents = function () {
        var self = this;
        this.lastIndex = 0;

        this.lightBox.listen('afterChange', function () {
            self._onAfterChange();
        });

        this.lightBox.listen('initialZoomInEnd', function () {
            self._onInitialZoomInEnd();
        });

        this.lightBox.listen('close', function () {
            var closingIndex = self.lightBox ? self.lightBox.getCurrentIndex() : NaN;

            // Snapshot the lightbox's own video element - currentTime/
            // paused/muted - before _resetAllVideos()/the state reset below
            // wipe it out, so the on-page video can resume from exactly
            // here instead of restarting from the beginning.
            var closingVideoEl = self._findCurrentVideoElement();
            var closingVideoState = closingVideoEl ? {
                currentTime: closingVideoEl.currentTime,
                paused: closingVideoEl.paused,
                userSetMuted: closingVideoEl.muted
            } : null;

            self._resetAllVideos();
            self.videoPlaybackStates = {};
            self.lightBox = null;

            //self._syncMainViewToIndex(closingIndex);
            self._syncMainViewToIndex(closingIndex, closingVideoState);
        });

        this.lightBox.listen('destroy', function () {
            self._onDestroy();
        });

        this.lightBox.listen('dragEnd', function () {
            var currentIndex = self.lightBox ? self.lightBox.getCurrentIndex() : 0;
            setTimeout(function () {
                self._moveIndicator(currentIndex);
            }, 200);
        });
    };

    /**
     * Reads the slide index actually rendered in the main image/video area
     * right now, via the data-active-slide-index that image-viewer.js's
     * showMediaInMainArea() sets whenever it (re)renders that area.
     *
     * Deliberately NOT based on '.slick-current': for a video thumbnail,
     * that highlight gets updated on click before the content itself does
     * (or, when the click opens the lightbox instead, without the content
     * changing at all) - see thumbnail_onClick() - so it can point at a
     * different slide than what's actually showing here.
     * @returns {number} the active index, or -1 if it can't be determined
     */
    ProductPhotoswipeLightbox.prototype._getActiveMainIndex = function () {
        //var $activeImg = $('.slick-current').find('img[data-slide-index]').first();
        //var index = parseInt($activeImg.attr('data-slide-index'), 10);

        var mainContainer = document.querySelector('.main-image-container');
        var index = mainContainer ? parseInt(mainContainer.dataset.activeSlideIndex, 10) : NaN;
        
        return isNaN(index) ? -1 : index;
    };

    /**
     * Mirrors the lightbox's current slide back onto the on-page carousel +
     * main image container when the lightbox closes, so the two stay in
     * sync in both directions. Reuses image-viewer.js's own media-display
     * logic (image swap, video swap/resume, '.slick-current' bookkeeping)
     * by dispatching a 'sync-main-view' event that it listens for,
     * carrying the target thumbnail plus (for a video) the exact playback
     * state to resume from.
     *
     * A real click on the thumbnail won't do here: video thumbnails are
     * also .prlightbox triggers (see thumbnail-video-template in
     * ImageViewer.ascx), so a synthetic click would bubble into that
     * delegated handler too and immediately reopen the lightbox it just
     * closed.
     * @param {number} index
     * @param {?object} videoState - {currentTime, paused, userSetMuted},
     *   or null when the closing slide wasn't a (hosted) video.
     */
    //ProductPhotoswipeLightbox.prototype._syncMainViewToIndex = function (index) {
    ProductPhotoswipeLightbox.prototype._syncMainViewToIndex = function (index, videoState) {
        if (isNaN(index) || index < 0 || index === this._getActiveMainIndex()) {
            return;
        }

        var thumbImg = $(SELECTORS.thumbnailItem)
            .find('img[data-slide-index="' + index + '"]')
            .first()
            .get(0);

        if (thumbImg) {
            //thumbImg.click();
            $(document).trigger('sync-main-view', [{ thumbImg: thumbImg, videoState: videoState || null }]);
        }
    };

    ProductPhotoswipeLightbox.prototype._onAfterChange = function () {
        var currentIndex = this.lightBox.getCurrentIndex();
        var currentItem = this.items[currentIndex];
        var totalItems = this.items.length;

        var isFirstToLast = this.lastIndex === 0 && currentIndex === totalItems - 1;
        var isLastToFirst = this.lastIndex === totalItems - 1 && currentIndex === 0;
        var isLooping = isFirstToLast || isLastToFirst;

        $('.' + CLASSES.fadeIn + ', .' + CLASSES.fadeInFast).removeClass(
            CLASSES.fadeIn + ' ' + CLASSES.fadeInFast
        );

        var fadeClass = isLooping ? CLASSES.fadeInFast : CLASSES.fadeIn;
        var delay = isLooping ? 0 : 100;
        var self = this;

        setTimeout(function () {
            self._applyFadeClass(fadeClass);
        }, delay);

        this.lastIndex = currentIndex;

        this._pauseAllVideos();
        this._setActiveThumbnail(currentIndex);
        this._moveIndicator(currentIndex);

        if (currentItem && currentItem.isVideo) {
            setTimeout(function () {
                self._playVideo(currentIndex);
            }, 300);
        }
    };

    ProductPhotoswipeLightbox.prototype._onInitialZoomInEnd = function () {
        $('body').addClass(CLASSES.bodyOpen);

        var currentIndex = this.lightBox.getCurrentIndex();
        var currentItem = this.items[currentIndex];

        this._applyFadeClass(CLASSES.fadeIn);
        this._setActiveThumbnail(currentIndex);
        this._moveIndicator(currentIndex);

        if (currentItem && currentItem.isVideo) {
            var self = this;
            setTimeout(function () {
                self._playVideo(currentIndex);
            }, 500);
        }
    };

    ProductPhotoswipeLightbox.prototype._onDestroy = function () {
        $('body').removeClass(CLASSES.bodyOpen);

        setTimeout(function () {
            var $pswpElement = $(SELECTORS.root);
            if ($pswpElement.length) {
                $pswpElement.attr('aria-hidden', 'true');
                $pswpElement.attr('class', 'pswp');
                $pswpElement.find('.pswp__item').empty();
            }
        }, 100);

        this.lightBox = null;
    };

    ProductPhotoswipeLightbox.prototype._applyFadeClass = function (fadeClass) {
        if (this.lightBox && this.lightBox.currItem && this.lightBox.currItem.container) {
            var $parentItem = $(this.lightBox.currItem.container).parent('.pswp__item');
            $parentItem.addClass(fadeClass);
        }
    };

    /* ------------------------------------------------------------------------
       Video playback
       ------------------------------------------------------------------------ */

    ProductPhotoswipeLightbox.prototype._playVideo = function (index) {
        var item = this.items[index];
        if (!item || !item.isVideo) {
            return;
        }

        // YouTube/iframe embeds autoplay on their own; only local <video> needs help.
        if (item.html && item.html.indexOf('<iframe') !== -1) {
            return;
        }

        var self = this;
        setTimeout(function () {
            self._findVideoWithRetry(index, 1, 5);
        }, 100);
    };

    ProductPhotoswipeLightbox.prototype._findVideoWithRetry = function (index, attempt, maxAttempts) {
        var video = this._findCurrentVideoElement();
        var self = this;

        if (!video) {
            if (attempt < maxAttempts) {
                setTimeout(function () {
                    self._findVideoWithRetry(index, attempt + 1, maxAttempts);
                }, 200 * attempt);
            }
            return;
        }

        this._configureAndPlayVideo(video, index);
    };

    ProductPhotoswipeLightbox.prototype._configureAndPlayVideo = function (video, index) {
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'contain';

        var savedState = this.videoPlaybackStates[index];
        video.muted = savedState && savedState.userSetMuted !== undefined
            ? savedState.userSetMuted
            : false; // play with sound on first play, then respect the user's later choice

        if (savedState && savedState.currentTime > 0) {
            video.currentTime = savedState.currentTime;
        }

        if (!video._eventsSetup) {
            this._addVideoEventListeners(video, index);
            video._eventsSetup = true;
        }

        setTimeout(function () {
            video.play().catch(function () {
                video.setAttribute('controls', 'controls');
            });
        }, 200);
    };

    ProductPhotoswipeLightbox.prototype._findCurrentVideoElement = function () {
        if (this.lightBox && this.lightBox.currItem && this.lightBox.currItem.container) {
            var video = this.lightBox.currItem.container.querySelector('video');


            if (video) {
                return video;
            }
        }

        var activeSlide = document.querySelector('.pswp__item.pswp__item--active');
        if (activeSlide) {
            var activeVideo = activeSlide.querySelector('video');
            if (activeVideo) {
                return activeVideo;
            }
        }

        var allVideos = document.querySelectorAll('.pswp__item video');
        for (var i = 0; i < allVideos.length; i++) {
            var rect = allVideos[i].getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
                return allVideos[i];
            }
        }

        return null;
    };

    ProductPhotoswipeLightbox.prototype._addVideoEventListeners = function (video, index) {
        var self = this;

        if (video._boundEvents) {
            Object.keys(video._boundEvents).forEach(function (eventName) {
                video.removeEventListener(eventName, video._boundEvents[eventName]);
            });
        }

        var stateFor = function () {
            if (!self.videoPlaybackStates[index]) {
                self.videoPlaybackStates[index] = {};
            }
            return self.videoPlaybackStates[index];
        };

        var events = {
            timeupdate: function () {
                var state = stateFor();
                state.currentTime = video.currentTime;
                state.duration = video.duration;
            },
            pause: function () {
                var state = stateFor();
                state.currentTime = video.currentTime;
                state.paused = true;
            },
            play: function () {
                var state = stateFor();
                state.paused = false;
                state.completed = false;
            },
            ended: function () {
                var state = stateFor();
                state.completed = true;
                state.currentTime = 0;
            },
            volumechange: function () {
                stateFor().userSetMuted = video.muted;
            }
        };

        video._boundEvents = events;
        Object.keys(events).forEach(function (eventName) {
            video.addEventListener(eventName, events[eventName]);
        });
    };

    ProductPhotoswipeLightbox.prototype._pauseAllVideos = function () {
        if (!this.pswpEl) {
            return;
        }

        $(this.pswpEl).find('.pswp__item video').each(function () {
            if (!this.paused) {
                this.pause();
            }
        });

        $(this.pswpEl).find('.pswp__item iframe').each(function () {
            if (this.src && this.src.indexOf('youtube.com') !== -1) {
                this.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            }
        });
    };

    ProductPhotoswipeLightbox.prototype._resetAllVideos = function () {
        if (!this.pswpEl) {
            return;
        }

        $(this.pswpEl).find('.pswp__item video').each(function () {
            this.pause();
            this.currentTime = 0;
            if (this._boundEvents) {
                Object.keys(this._boundEvents).forEach(function (eventName) {
                    this.removeEventListener(eventName, this._boundEvents[eventName]);
                }.bind(this));
                this._boundEvents = null;
            }
            this._eventsSetup = false;
        });

        $(this.pswpEl).find('.pswp__item iframe').each(function () {
            if (this.src && this.src.indexOf('youtube.com') !== -1) {
                this.contentWindow.postMessage('{"event":"command","func":"stopVideo","args":""}', '*');
            }
        });
    };

    /* ------------------------------------------------------------------------
       Expose + auto-init
       ------------------------------------------------------------------------ */

    window.ProductPhotoswipeLightbox = ProductPhotoswipeLightbox;

    $(function () {
        if ($(SELECTORS.root).length) {
            var instance = new ProductPhotoswipeLightbox(SELECTORS.root).init();
            window.productPhotoswipeLightboxInstance = instance;
            instance._openFromShareLinkIfPresent();
        }
    });
})(jQuery, window, document);